import { useEffect, useMemo, useState, useCallback } from "react";
import { agentApi, domainTemplateApi } from "../../../api/client";
import { COLORS } from "../../../theme/colors";
import { MATERIALS } from "../../../theme/materials";
import {
  CHUNK_PRESETS,
  DEFAULT_CHUNK_SIZE,
  DEFAULT_CHUNK_OVERLAP,
  DEFAULT_PRESET_ID,
  presetById,
  presetIdForValues,
} from "../../ingestion/chunkTuning";

// Family-level knowledge settings cascade for supervisors. Pass 1 of
// the Knowledge simplification design (per the conversation
// 2026-05-05). Three rows: domain template, embedding model,
// processing config. Each shows the supervisor's value as the
// family default, lists experts that have diverged, and offers a
// "Sync to all" action that fans out to per-agent
// PUT /api/agents/<id>/knowledge/settings calls.
//
// Embedding lock is the special case: an expert that has already
// ingested chunks has its embedding model SEALED at the row
// level (agents.embedding_model_lock). Sync into a sealed expert is
// refused server-side. The panel shows lock state per expert so
// the user knows up front, and the sync button reports
// "synced N/M, skipped K (locked)" rather than failing silently.
//
// Pure UI — no backend changes. Reuses existing
// getKnowledgeSettings + updateKnowledgeSettings endpoints.
//
// Hidden for non-supervisors and supervisors without experts.

const ROW_PAD = 14;

function pillStyle(color) {
  return {
    padding: "2px 8px", borderRadius: 999, fontSize: 10, fontWeight: 600,
    background: `${color}20`, color, border: `1px solid ${color}55`,
    whiteSpace: "nowrap",
  };
}

function buttonStyle(active, disabled) {
  return {
    padding: "5px 12px", fontSize: 11, fontWeight: 600,
    borderRadius: 6,
    cursor: disabled ? "default" : "pointer",
    background: active ? COLORS.accent : "transparent",
    border: `1px solid ${active ? COLORS.accent : COLORS.border}`,
    color: active ? "#fff" : COLORS.textMuted,
    opacity: disabled ? 0.5 : 1,
    whiteSpace: "nowrap",
  };
}

const Card = ({ title, hint, children, footer }) => (
  <div style={{
    padding: 16, borderRadius: 12,
    border: `1px solid ${COLORS.border}`, background: COLORS.surface,
    boxShadow: MATERIALS.spatialShadow(),
    display: "flex", flexDirection: "column", gap: 12,
  }}>
    <div>
      <div style={{
        fontSize: 11, fontWeight: 700, color: COLORS.textDim,
        textTransform: "uppercase", letterSpacing: "0.06em",
      }}>{title}</div>
      {hint && <div style={{ fontSize: 11, color: COLORS.textMuted, marginTop: 4 }}>{hint}</div>}
    </div>
    {children}
    {footer && (
      <div style={{ borderTop: `1px solid ${COLORS.border}55`, paddingTop: 10 }}>
        {footer}
      </div>
    )}
  </div>
);

// Exported for unit testing
export function presetLabelOf(presetId) {
  const p = CHUNK_PRESETS.find(x => x.id === presetId);
  return p ? p.label : presetId;
}

// Compute a single string descriptor for a settings dimension so
// we can compare supervisor vs each expert with one === check.
// Exported for unit testing
export function descTemplate(s)        { return s?.template || "generic"; }
// Exported for unit testing
export function descEmbedding(s)       {
  // Lock value (immutable post-ingest) wins over flow_config draft;
  // settings.embedding_model_lock is the source-of-truth.
  return s?.embedding_model_lock || s?.knowledge?.embedding_model || "(unset)";
}
// Exported for unit testing
export function descProcessing(s) {
  const ing = s?.ingestion || {};
  const cs = ing.chunk_size ?? DEFAULT_CHUNK_SIZE;
  const co = ing.chunk_overlap ?? DEFAULT_CHUNK_OVERLAP;
  const pid = ing.preset || presetIdForValues(cs, co);
  return `${presetLabelOf(pid)} · cs=${cs} · co=${co}`;
}

export default function KnowledgeFamilyPanel({ tree, currentUser }) {
  const supervisor = tree?.agent;
  const experts = useMemo(
    () => (tree?.experts || []).map(e => ({ id: e.id, name: e.name })),
    [tree?.experts]
  );
  const isSupervisor = (tree?.agent_kind || tree?.agent?.metadata_json?.agent_kind) === "supervisor";
  const canEdit = ["owner", "administrator", "developer"].includes(currentUser?.role);

  // Settings per agent: { [agentId]: { knowledge, ingestion, template, embedding_model_lock, embedding_migration_state } }
  const [settings, setSettings] = useState({});
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [syncing, setSyncing] = useState(null); // 'template' | 'embedding' | 'processing' | null
  const [syncReport, setSyncReport] = useState(null); // {section, synced, skipped[], failed[]}

  const [templateCatalog, setTemplateCatalog] = useState([]);

  const refresh = useCallback(async () => {
    if (!supervisor?.id || experts.length === 0) return;
    setLoading(true); setErr(null);
    try {
      // Sequential getKnowledgeSettings (NOT Promise.all). Same
      // CNPG connection-pool exhaustion concern as ScopedBindingsList —
      // N parallel reads against tenant_settings + agents + flow_config
      // overwhelmed the pool. Sequential ~150ms × N is acceptable
      // (a 5-member family ~750ms total).
      const ids = [supervisor.id, ...experts.map(e => e.id)];
      const next = {};
      for (const id of ids) {
        try {
          const r = await agentApi.getKnowledgeSettings(id);
          next[id] = r?.data || null;
        } catch (e) {
          // eslint-disable-next-line no-console
          console.warn(`getKnowledgeSettings(${id}) failed:`, e);
          next[id] = null;
        }
      }
      setSettings(next);
    } catch (e) {
      setErr(e?.message || String(e));
    } finally {
      setLoading(false);
    }
  }, [supervisor?.id, experts]);

  useEffect(() => {
    refresh().catch(() => {});
  }, [refresh]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await domainTemplateApi.list();
        if (!cancelled) setTemplateCatalog(Array.isArray(r?.templates) ? r.templates : []);
      } catch {
        if (!cancelled) setTemplateCatalog([]);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  if (!isSupervisor || experts.length === 0) return null;

  const supSettings = settings[supervisor.id];

  // ── Diff computation per dimension ─────────────────────────────────
  // For each expert, we precompute three booleans + lock status so
  // the row renderers stay declarative.
  const rows = useMemo(() => {
    if (!supSettings) return [];
    const supT = descTemplate(supSettings);
    const supE = descEmbedding(supSettings);
    const supP = descProcessing(supSettings);
    return experts.map(e => {
      const s = settings[e.id];
      if (!s) return { id: e.id, name: e.name, missing: true };
      return {
        id: e.id, name: e.name,
        template: descTemplate(s),
        templateOverride: descTemplate(s) !== supT,
        embedding: descEmbedding(s),
        embeddingOverride: descEmbedding(s) !== supE,
        embeddingLocked: !!s.embedding_model_lock,
        processing: descProcessing(s),
        processingOverride: descProcessing(s) !== supP,
      };
    });
  }, [supSettings, settings, experts]);

  // ── Sync handlers ──────────────────────────────────────────────────
  // Each handler runs N PUTs in parallel, collects per-expert outcomes,
  // and reports a structured summary the user can read at a glance.
  const fanOut = useCallback(async (section, mkPayloadForExpert, predicate) => {
    setSyncing(section); setSyncReport(null);
    const synced = [];
    const skipped = [];
    const failed = [];
    await Promise.all(experts.map(async (e) => {
      const exSettings = settings[e.id];
      if (!exSettings) {
        skipped.push({ id: e.id, name: e.name, reason: "settings load failed" });
        return;
      }
      const decision = predicate ? predicate(exSettings, supSettings) : { proceed: true };
      if (!decision.proceed) {
        skipped.push({ id: e.id, name: e.name, reason: decision.reason || "skipped" });
        return;
      }
      try {
        await agentApi.updateKnowledgeSettings(e.id, mkPayloadForExpert(exSettings, supSettings));
        synced.push({ id: e.id, name: e.name });
      } catch (err) {
        failed.push({ id: e.id, name: e.name, reason: err?.message || String(err) });
      }
    }));
    setSyncReport({ section, synced, skipped, failed });
    setSyncing(null);
    refresh();
  }, [experts, settings, supSettings, refresh]);

  const syncTemplate = useCallback(() => {
    const t = descTemplate(supSettings);
    return fanOut(
      "template",
      () => ({ template: t }),
      (ex) => descTemplate(ex) === t
        ? { proceed: false, reason: "already matches family default" }
        : { proceed: true },
    );
  }, [fanOut, supSettings]);

  const syncEmbedding = useCallback(() => {
    const supEmbedding = supSettings?.knowledge?.embedding_model;
    const supProvider  = supSettings?.knowledge?.embedding_provider;
    if (!supEmbedding) return;
    return fanOut(
      "embedding",
      () => ({ knowledge: { embedding_model: supEmbedding, embedding_provider: supProvider } }),
      (ex) => {
        // Skip experts with a sealed lock — even if the lock matches
        // we don't need to write; if it differs, the server will refuse.
        if (ex.embedding_model_lock) {
          if (ex.embedding_model_lock === supEmbedding) {
            return { proceed: false, reason: "already locked to this model" };
          }
          return { proceed: false, reason: `locked to ${ex.embedding_model_lock} — use Migrate Embeddings flow` };
        }
        const cur = ex?.knowledge?.embedding_model;
        if (cur === supEmbedding) {
          return { proceed: false, reason: "already matches" };
        }
        return { proceed: true };
      },
    );
  }, [fanOut, supSettings]);

  const syncProcessing = useCallback(() => {
    const ing = supSettings?.ingestion || {};
    const cs = ing.chunk_size ?? DEFAULT_CHUNK_SIZE;
    const co = ing.chunk_overlap ?? DEFAULT_CHUNK_OVERLAP;
    const preset = ing.preset || presetIdForValues(cs, co);
    return fanOut(
      "processing",
      () => ({ ingestion: { preset, chunk_size: cs, chunk_overlap: co } }),
      (ex) => descProcessing(ex) === descProcessing(supSettings)
        ? { proceed: false, reason: "already matches" }
        : { proceed: true },
    );
  }, [fanOut, supSettings]);

  // ── Render ─────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div style={{ color: COLORS.textDim, fontSize: 12, padding: 16 }}>
        Loading family settings…
      </div>
    );
  }
  if (err) {
    return (
      <div style={{ color: COLORS.pink, fontSize: 12, padding: 16 }}>
        Failed to load family settings: {err}
      </div>
    );
  }
  if (!supSettings) {
    return (
      <div style={{ color: COLORS.textDim, fontSize: 12, padding: 16 }}>
        Supervisor settings unavailable.
      </div>
    );
  }

  // ── Helper render for a single override row in a card ──────────────
  const OverrideRow = ({ row, dimension, value, isOverride, locked }) => (
    <div style={{
      display: "flex", alignItems: "center", gap: 10,
      padding: "8px 10px", borderRadius: 6,
      background: isOverride ? "#f59e0b15" : "transparent",
      borderLeft: isOverride ? `3px solid #f59e0b` : "3px solid transparent",
    }}>
      <span style={{
        fontSize: 9, fontWeight: 700, color: COLORS.textDim,
        padding: "2px 6px", borderRadius: 4,
        border: `1px solid ${COLORS.border}`,
        fontFamily: "monospace",
      }}>EXP</span>
      <span style={{ fontSize: 12, color: COLORS.text, flex: 1, minWidth: 0,
        overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
        {row.name}
      </span>
      {locked && <span style={pillStyle(COLORS.pink)}>🔒 locked</span>}
      <span style={{ fontSize: 11, color: isOverride ? "#d97706" : COLORS.textDim,
        fontFamily: "monospace", whiteSpace: "nowrap" }}>
        {value}
      </span>
    </div>
  );

  const reportFor = (section) => syncReport?.section === section ? syncReport : null;

  const Report = ({ r }) => {
    if (!r) return null;
    const total = r.synced.length + r.skipped.length + r.failed.length;
    return (
      <div style={{
        fontSize: 11, color: COLORS.text,
        padding: "8px 10px", borderRadius: 6,
        background: r.failed.length > 0 ? `${COLORS.pink}1A` : `${COLORS.green}1A`,
        border: `1px solid ${r.failed.length > 0 ? COLORS.pink : COLORS.green}55`,
      }}>
        <div style={{ fontWeight: 700, marginBottom: 4 }}>
          Synced {r.synced.length} of {total}
          {r.skipped.length > 0 && ` · ${r.skipped.length} skipped`}
          {r.failed.length > 0 && ` · ${r.failed.length} failed`}
        </div>
        {r.skipped.length > 0 && (
          <div style={{ color: COLORS.textMuted }}>
            {r.skipped.map(x => `${x.name} (${x.reason})`).join(" · ")}
          </div>
        )}
        {r.failed.length > 0 && (
          <div style={{ color: COLORS.pink }}>
            {r.failed.map(x => `${x.name}: ${x.reason}`).join(" · ")}
          </div>
        )}
      </div>
    );
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      {/* Header */}
      <div>
        <div style={{ fontSize: 14, fontWeight: 700, color: COLORS.text }}>
          Family knowledge settings
        </div>
        <div style={{ fontSize: 12, color: COLORS.textMuted, marginTop: 4 }}>
          The supervisor's settings are shown as the family default. Each
          expert's value is listed below — amber rows have diverged. "Sync
          to all" propagates the supervisor's value to every expert that
          isn't sealed by an embedding lock or already-matching.
        </div>
      </div>

      {/* ── Domain template ─────────────────────────────────────────── */}
      <Card
        title="Domain template"
        hint="Drives ingestion-time graph extraction (entities + relationships) per the marketplace catalog. Set on the supervisor's Settings sub-tab."
      >
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
          <div>
            <div style={{ fontSize: 11, color: COLORS.textDim, marginBottom: 4 }}>Family default</div>
            <div style={{ fontFamily: "monospace", fontSize: 13, color: COLORS.text }}>
              {descTemplate(supSettings)}
            </div>
          </div>
          <button type="button"
            disabled={!canEdit || syncing !== null || rows.every(r => !r.templateOverride)}
            onClick={syncTemplate}
            style={buttonStyle(rows.some(r => r.templateOverride),
              !canEdit || syncing !== null || rows.every(r => !r.templateOverride))}
            title={!canEdit ? "Read-only role" : undefined}>
            {syncing === "template" ? "Syncing…" : "Sync to all experts"}
          </button>
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
          {rows.map(r => (
            <OverrideRow key={r.id} row={r} dimension="template"
              value={r.template} isOverride={r.templateOverride} locked={false} />
          ))}
        </div>
        <Report r={reportFor("template")} />
      </Card>

      {/* ── Embedding model ─────────────────────────────────────────── */}
      <Card
        title="Embedding model"
        hint="Per-agent — once an agent has ingested any chunks, the model is sealed (immutable). Sync skips sealed experts; use the per-agent Migrate Embeddings flow to change a sealed expert."
      >
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
          <div>
            <div style={{ fontSize: 11, color: COLORS.textDim, marginBottom: 4 }}>Family default</div>
            <div style={{ fontFamily: "monospace", fontSize: 13, color: COLORS.text }}>
              {descEmbedding(supSettings)}
              {supSettings.embedding_model_lock && (
                <span style={{ ...pillStyle(COLORS.pink), marginLeft: 8 }}>🔒 sealed</span>
              )}
            </div>
          </div>
          <button type="button"
            disabled={!canEdit || syncing !== null || !supSettings?.knowledge?.embedding_model
              || rows.every(r => !r.embeddingOverride || r.embeddingLocked)}
            onClick={syncEmbedding}
            style={buttonStyle(rows.some(r => r.embeddingOverride && !r.embeddingLocked),
              !canEdit || syncing !== null || !supSettings?.knowledge?.embedding_model
              || rows.every(r => !r.embeddingOverride || r.embeddingLocked))}
            title={!supSettings?.knowledge?.embedding_model
              ? "Set the supervisor's embedding model first"
              : !canEdit ? "Read-only role" : undefined}>
            {syncing === "embedding" ? "Syncing…" : "Sync to compatible experts"}
          </button>
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
          {rows.map(r => (
            <OverrideRow key={r.id} row={r} dimension="embedding"
              value={r.embedding} isOverride={r.embeddingOverride}
              locked={r.embeddingLocked} />
          ))}
        </div>
        <Report r={reportFor("embedding")} />
      </Card>

      {/* ── Processing config ───────────────────────────────────────── */}
      <Card
        title="Processing configuration"
        hint="Chunk size, chunk overlap, preset. Applied at ingestion time — existing chunks aren't re-processed by changing this. Sync only updates the expert's setting; reingest if you want existing chunks re-cut."
      >
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
          <div>
            <div style={{ fontSize: 11, color: COLORS.textDim, marginBottom: 4 }}>Family default</div>
            <div style={{ fontFamily: "monospace", fontSize: 13, color: COLORS.text }}>
              {descProcessing(supSettings)}
            </div>
          </div>
          <button type="button"
            disabled={!canEdit || syncing !== null || rows.every(r => !r.processingOverride)}
            onClick={syncProcessing}
            style={buttonStyle(rows.some(r => r.processingOverride),
              !canEdit || syncing !== null || rows.every(r => !r.processingOverride))}>
            {syncing === "processing" ? "Syncing…" : "Sync to all experts"}
          </button>
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
          {rows.map(r => (
            <OverrideRow key={r.id} row={r} dimension="processing"
              value={r.processing} isOverride={r.processingOverride} locked={false} />
          ))}
        </div>
        <Report r={reportFor("processing")} />
      </Card>

      {/* Footnote pointing at the existing per-agent panel for actually
          changing these values. The cascade panel doesn't author values
          itself; it propagates whatever the supervisor's per-agent
          Settings tab has been set to. Keeps authoring + cascade
          concerns separate. */}
      <div style={{ fontSize: 11, color: COLORS.textMuted, padding: "0 4px" }}>
        Note: this panel reads the supervisor's per-agent settings as the source
        of truth. To change the family default, edit the supervisor's
        <strong> Settings</strong> sub-tab — the values here update
        automatically. Per-expert overrides are still editable from each
        expert's own Knowledge tab if needed.
      </div>
    </div>
  );
}
